class InstagramFeed extends HTMLElement {
  static get observedAttributes() {
    return ['data-limit', 'data-endpoint'];
  }

  connectedCallback() {
    this.load();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.load();
  }

  disconnectedCallback() {
    this.destroyDrag();
  }

  get endpoint() {
    return this.getAttribute('data-endpoint') || '';
  }

  get limit() {
    return parseInt(this.getAttribute('data-limit'), 10) || 12;
  }

  async load() {
    this.destroyDrag();

    if (!this.endpoint) {
      this.renderMessage('Instagram feed URL is not configured.');
      return;
    }

    const url = new URL(this.endpoint);
    url.searchParams.set('limit', this.limit);

    try {
      const res = await fetch(url.toString(), { headers: { Accept: 'application/json' } });
      const data = await res.json();
      const posts = Array.isArray(data.data) ? data.data : [];
      if (!posts.length) {
        this.renderMessage('No posts available or an error occurred.');
        return;
      }
      this.renderSlider(posts);
    } catch (err) {
      this.renderMessage('No posts available or an error occurred.');
    }
  }

  renderMessage(message) {
    this.innerHTML = '<p class="c-instagram-feed__message">' + escapeHtml(message) + '</p>';
  }

  renderSlider(posts) {
    var row1Posts = [];
    var row2Posts = [];
    for (var i = 0; i < posts.length; i++) {
      if (i % 2 === 0) row1Posts.push(posts[i]);
      else row2Posts.push(posts[i]);
    }

    var row1Html = row1Posts.map(function(p) { return this.renderItem(p); }.bind(this)).filter(Boolean).join('');
    var row2Html = row2Posts.map(function(p) { return this.renderItem(p); }.bind(this)).filter(Boolean).join('');

    this.innerHTML =
      '<div class="c-instagram-feed__slider">' +
        '<div class="c-instagram-feed__row">' + row1Html + '</div>' +
        (row2Html ? '<div class="c-instagram-feed__row">' + row2Html + '</div>' : '') +
      '</div>';

    this.initDrag();
  }

  renderItem(post) {
    var media = this.renderMediaElement(post);
    if (!media) return '';

    var permalink = post.permalink ? escapeAttr(post.permalink) : '#';

    return (
      '<div class="c-instagram-post__item">' +
        '<a class="c-instagram-post__link" href="' + permalink + '" target="_blank" rel="noopener">' +
          media +
        '</a>' +
      '</div>'
    );
  }

  renderMediaElement(post) {
    var type = post.media_type || '';

    if (type === 'IMAGE' || type === 'CAROUSEL_ALBUM') {
      if (!post.media_url) return '';
      return '<div class="c-instagram-post__image" style="background-image:url(' + escapeCssUrl(post.media_url) + ')"></div>';
    }

    if (type === 'VIDEO') {
      if (post.thumbnail_url) {
        return '<div class="c-instagram-post__image" style="background-image:url(' + escapeCssUrl(post.thumbnail_url) + ')"></div>';
      }
      if (post.media_url) {
        return '<video class="c-instagram-post__image" src="' + escapeAttr(post.media_url) + '" muted playsinline preload="metadata"></video>';
      }
      return '';
    }

    return '';
  }

  destroyDrag() {
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = null;
    }
  }

  initDrag() {
    var slider = this.querySelector('.c-instagram-feed__slider');
    var rows = this.querySelectorAll('.c-instagram-feed__row');
    if (!slider || !rows.length) return;

    var translateX = 0;
    var maxDrag = 0;
    var isDragging = false;
    var startX = 0;
    var startTranslate = 0;
    var velocity = 0;
    var lastX = 0;
    var lastTime = 0;
    var animFrame = null;
    var dragDistance = 0;

    function recalc() {
      var widest = 0;
      rows.forEach(function(row) {
        if (row.scrollWidth > widest) widest = row.scrollWidth;
      });
      maxDrag = Math.max(0, widest - slider.offsetWidth);
      translateX = clamp(translateX);
      applyTranslate();
    }

    function clamp(val) {
      return Math.max(-maxDrag, Math.min(0, val));
    }

    function applyTranslate() {
      var t = 'translate3d(' + translateX + 'px, 0, 0)';
      rows.forEach(function(row) { row.style.transform = t; });
    }

    function center() {
      translateX = -(maxDrag / 2);
      applyTranslate();
    }

    function onMouseDown(e) {
      if (animFrame) { cancelAnimationFrame(animFrame); animFrame = null; }
      isDragging = true;
      dragDistance = 0;
      startX = e.clientX;
      startTranslate = translateX;
      lastX = startX;
      lastTime = Date.now();
      velocity = 0;
      slider.classList.add('is-dragging');
    }

    function onTouchStart(e) {
      if (animFrame) { cancelAnimationFrame(animFrame); animFrame = null; }
      isDragging = true;
      dragDistance = 0;
      startX = e.touches[0].clientX;
      startTranslate = translateX;
      lastX = startX;
      lastTime = Date.now();
      velocity = 0;
      slider.classList.add('is-dragging');
    }

    function onMouseMove(e) {
      if (!isDragging) return;
      var x = e.clientX;
      track(x);
      translateX = clamp(startTranslate + (x - startX));
      applyTranslate();
    }

    function onTouchMove(e) {
      if (!isDragging) return;
      var x = e.touches[0].clientX;
      var dx = Math.abs(x - startX);
      if (dx > 8) e.preventDefault();
      track(x);
      translateX = clamp(startTranslate + (x - startX));
      applyTranslate();
    }

    function track(x) {
      var now = Date.now();
      var dt = now - lastTime;
      dragDistance += Math.abs(x - lastX);
      if (dt > 0) velocity = (x - lastX) / dt;
      lastX = x;
      lastTime = now;
    }

    function onEnd() {
      if (!isDragging) return;
      isDragging = false;
      slider.classList.remove('is-dragging');
      momentum();
    }

    function momentum() {
      velocity *= 0.95;
      if (Math.abs(velocity) < 0.05) return;
      translateX = clamp(translateX + velocity * 16);
      applyTranslate();
      animFrame = requestAnimationFrame(momentum);
    }

    function onClick(e) {
      if (dragDistance > 6) e.preventDefault();
    }

    slider.addEventListener('mousedown', onMouseDown);
    slider.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('mouseup', onEnd);
    window.addEventListener('touchend', onEnd);
    slider.addEventListener('click', onClick, true);

    var resizeObserver = new ResizeObserver(function() { recalc(); });
    resizeObserver.observe(slider);

    recalc();
    center();

    this._cleanup = function() {
      if (animFrame) cancelAnimationFrame(animFrame);
      slider.removeEventListener('mousedown', onMouseDown);
      slider.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', onEnd);
      window.removeEventListener('touchend', onEnd);
      slider.removeEventListener('click', onClick, true);
      resizeObserver.disconnect();
    };
  }
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeAttr(str) {
  return escapeHtml(str);
}

function escapeCssUrl(str) {
  var value = String(str);
  if (!/^https?:\/\//i.test(value)) return '';
  return value.replace(/["'()\\]/g, '');
}

if (!customElements.get('instagram-feed')) {
  customElements.define('instagram-feed', InstagramFeed);
}
